import type { CHANNEL_KIND } from '../../lib/rcu/catalog/channel';
import { TARGET_VALUE_SPECS, isValueInRange } from './catalog/value';
import type { TargetValueKind } from './catalog/value';

/**
 * The outputs a key state may drive.
 *
 * This is the feature's only view of the rest of the app: a list of controllable
 * outputs, each already resolved into the way a mapping action spells it. The
 * feature never asks what a load *is* — only how to name it, how to read its
 * value, and which selector its action carries.
 */
/**
 * How a relay action names its output: `relay:value`. The kind is the app's own
 * channel vocabulary, so an output is called the same thing here as everywhere
 * else.
 */
export interface RelaySelector {
  readonly kind: typeof CHANNEL_KIND.RCU;
  /** 1-based relay number, as `rcuoperate` and the mapping both count. */
  readonly relay: number;
}

/** How a DIN action names its output: `module,address,channel,value`. */
export interface DinSelector {
  readonly kind: typeof CHANNEL_KIND.DIN;
  /** The module's low id in decimal, which is what a mapping action carries. */
  readonly module: number;
  /** 0–15, the module's rotary address. */
  readonly address: number;
  /** 0-based, the channel as the firmware counts it. */
  readonly channel: number;
}

export type TargetSelector = RelaySelector | DinSelector;

export interface MappingTarget {
  /** The outside world's own id for this output; the feature stores only this. */
  readonly id: string;
  /** Name of the load wired to it. */
  readonly label: string;
  /** Where it sits, e.g. `RCU output 12` or `Phase cut @3 · channel 2`. */
  readonly detail: string;
  readonly valueKind: TargetValueKind;
  readonly selector: TargetSelector;
}

/** Finds a target by id, so a stored state can be checked against the outputs. */
export const findTarget = (
  targets: readonly MappingTarget[],
  targetId: string,
): MappingTarget | undefined => targets.find((target) => target.id === targetId);

/** True when the value fits the output it is stored against. */
export const valueFitsTarget = (target: MappingTarget, value: number): boolean =>
  isValueInRange(target.valueKind, value);

/** The value a target starts at when a state first includes it. */
export const defaultValueForTarget = (target: MappingTarget): number =>
  TARGET_VALUE_SPECS[target.valueKind].defaultValue;
